import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildDakkapelSvg } from '../lib/dakkapelSvg';
import { useTranslation } from '../context/TranslationContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DAKKAPEL_ELEMENT_TYPES,
  DAKKAPEL_MIN_ELEMENT_WIDTH_MM,
  getDakkapelElementMaxWidthMm,
  DAKKAPEL_MIN_WIDTH_MM,
  DAKKAPEL_MAX_WIDTH_MM,
  DAKKAPEL_MIN_HEIGHT_MM,
  DAKKAPEL_MAX_HEIGHT_MM,
  normalizeDakkapelElementType,
} from '../data/dakkapelOptions';

const DIMENSION_LIMITS = {
  width: { min: DAKKAPEL_MIN_WIDTH_MM, max: DAKKAPEL_MAX_WIDTH_MM },
  height: { min: DAKKAPEL_MIN_HEIGHT_MM, max: DAKKAPEL_MAX_HEIGHT_MM },
};

const DakkapelDrawingBoard = ({
  config,
  onElementWidthChange,
  onElementTypeChange,
  onWidthChange,
  onHeightChange,
}) => {
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const [viewSize, setViewSize] = useState({ width: 720, height: 520 });
  const [openIndex, setOpenIndex] = useState(null);

  // Match the SVG viewBox to the panel so wide previews don't letterbox the
  // elevation into a small centered strip (dakkapel.nl fills the half-width).
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return undefined;

    let frame = 0;
    const update = () => {
      frame = 0;
      const w = Math.max(320, Math.round(root.clientWidth));
      const h = Math.max(280, Math.round(root.clientHeight));
      setViewSize((prev) =>
        Math.abs(prev.width - w) < 2 && Math.abs(prev.height - h) < 2 ? prev : { width: w, height: h }
      );
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    scheduleUpdate();
    const ro = new ResizeObserver(scheduleUpdate);
    ro.observe(root);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, []);

  const svgMarkup = useMemo(
    () =>
      buildDakkapelSvg(config || {}, {
        viewWidth: viewSize.width,
        viewHeight: viewSize.height,
        showDimensions: true,
        interactive: true,
      }),
    [config, viewSize.width, viewSize.height]
  );

  // Set SVG via DOM, not dangerouslySetInnerHTML — React must not reconcile
  // the foreignObject inputs or it throws removeChild when the markup changes.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const frame = window.requestAnimationFrame(() => {
      if (rootRef.current) rootRef.current.innerHTML = svgMarkup;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [svgMarkup]);

  // The SVG is injected markup, so its inputs and click targets are reached by
  // delegation rather than React handlers.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const revert = (input, value) => {
      if (value != null) input.value = String(Math.round(value));
    };

    const commitPartWidth = (input) => {
      const index = Number(input.getAttribute('data-dakkapel-part-width'));
      if (Number.isNaN(index)) return;
      const parsed = parseInt(input.value, 10);
      if (Number.isNaN(parsed)) {
        revert(input, config?.elements?.[index]?.widthMm);
        return;
      }
      if (onElementWidthChange) onElementWidthChange(index, parsed);
    };

    const commitDimension = (input) => {
      const axis = input.getAttribute('data-dakkapel-dim');
      const limits = DIMENSION_LIMITS[axis];
      const handler = axis === 'width' ? onWidthChange : onHeightChange;
      const current = axis === 'width' ? config?.width : config?.height;
      if (!limits || !handler) return;
      const parsed = parseInt(input.value, 10);
      if (Number.isNaN(parsed) || parsed < limits.min || parsed > limits.max) {
        revert(input, current);
        return;
      }
      handler(parsed);
    };

    const commit = (input) => {
      if (input.hasAttribute('data-dakkapel-part-width')) commitPartWidth(input);
      else if (input.hasAttribute('data-dakkapel-dim')) commitDimension(input);
    };

    const findInput = (event) =>
      event.target?.closest?.('input[data-dakkapel-part-width], input[data-dakkapel-dim]');

    const onFocusOut = (event) => {
      const input = findInput(event);
      if (input && root.contains(input)) commit(input);
    };

    const onKeyDown = (event) => {
      if (event.key !== 'Enter') return;
      const input = findInput(event);
      if (!input || !root.contains(input)) return;
      event.preventDefault();
      input.blur();
    };

    const onClick = (event) => {
      const hit = event.target?.closest?.('[data-dakkapel-element]');
      if (!hit || !root.contains(hit)) return;
      const index = Number(hit.getAttribute('data-dakkapel-element'));
      if (!Number.isNaN(index)) setOpenIndex(index);
    };

    root.addEventListener('focusout', onFocusOut);
    root.addEventListener('keydown', onKeyDown);
    root.addEventListener('click', onClick);
    return () => {
      root.removeEventListener('focusout', onFocusOut);
      root.removeEventListener('keydown', onKeyDown);
      root.removeEventListener('click', onClick);
    };
  }, [config, onElementWidthChange, onWidthChange, onHeightChange]);

  const elements = config?.elements || [];
  const openElement = openIndex != null ? elements[openIndex] : null;
  const openMaxWidth = getDakkapelElementMaxWidthMm(openIndex ?? -1, elements.length, config?.width);

  // Local copy so the field can be typed into freely before it is clamped.
  const [dialogWidth, setDialogWidth] = useState('');
  useEffect(() => {
    setDialogWidth(openElement ? String(Math.round(openElement.widthMm)) : '');
  }, [openElement, openIndex]);

  const commitDialogWidth = () => {
    if (!openElement || !onElementWidthChange) return;
    const parsed = parseInt(dialogWidth, 10);
    if (Number.isNaN(parsed)) {
      setDialogWidth(String(Math.round(openElement.widthMm)));
      return;
    }
    onElementWidthChange(openIndex, parsed);
  };

  return (
    <>
      <div
        ref={rootRef}
        className="dakkapel-drawing-board h-full min-h-[500px] w-full overflow-hidden bg-[#F5F5F5]"
      />

      <Dialog
        open={openElement != null}
        onOpenChange={(open) => {
          if (!open) setOpenIndex(null);
        }}
      >
        <DialogContent className="notranslate sm:max-w-sm" translate="no">
          <DialogHeader>
            <DialogTitle>{t('dakkapel.elementN', { n: (openIndex ?? 0) + 1 })}</DialogTitle>
            <DialogDescription>{t('dakkapel.step2Desc')}</DialogDescription>
          </DialogHeader>

          {openElement && (
            <div className="space-y-4">
              <div>
                <Label>{t('dakkapel.elementType')}</Label>
                <Select
                  value={normalizeDakkapelElementType(openElement.type)}
                  onValueChange={(value) => {
                    if (onElementTypeChange) onElementTypeChange(openIndex, value);
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAKKAPEL_ELEMENT_TYPES.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {t(type.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('dakkapel.elementWidthMm')}</Label>
                <Input
                  type="number"
                  min={DAKKAPEL_MIN_ELEMENT_WIDTH_MM}
                  max={openMaxWidth ?? undefined}
                  value={dialogWidth}
                  onChange={(e) => setDialogWidth(e.target.value)}
                  onBlur={commitDialogWidth}
                  onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                  className="mt-1"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DakkapelDrawingBoard;
